import { defineMessages } from 'react-intl';

import { createAction } from '@reduxjs/toolkit';

import api from 'mastodon/api';
import type { ApiDraftJSON, ApiDraftParams } from 'mastodon/api/drafts';
import { apiCreateDraft, apiUpdateDraft } from 'mastodon/api/drafts';
import type { ApiStatusJSON } from 'mastodon/api_types/statuses';
import { browserHistory } from 'mastodon/components/router';
import { createAppThunk } from 'mastodon/store/typed_functions';
import { notifyDraftsChanged } from 'mastodon/utils/drafts_changed';

import { showAlert, showAlertForError } from './alerts';
import { ensureComposeIsVisible, resetCompose } from './compose';
import { importFetchedStatus } from './importer';
import { resetNgWordsWarning } from './ng_words';

const messages = defineMessages({
  saved: { id: 'drafts.saved', defaultMessage: 'Draft saved.' },
  empty: { id: 'drafts.empty_post', defaultMessage: 'Write something first.' },
  view: { id: 'drafts.view', defaultMessage: 'View drafts' },
  replyGone: {
    id: 'drafts.reply_gone',
    defaultMessage:
      'The post this draft replied to is gone, so it was opened as a new post.',
  },
});

/** Puts a draft into the compose form, remembering which draft it was */
export const loadComposeDraft = createAction<ApiDraftJSON>('compose/loadDraft');

export const setComposeDraftId = createAction<string | null>(
  'compose/setDraftId',
);

// One save at a time, so clicking twice does not make two drafts
let savingDraft = false;

/**
 * Saves what is in the compose form as a draft (or updates its draft), then
 * empties the form so the next post starts fresh
 */
export const saveComposeDraft = createAppThunk(
  async (_arg, { dispatch, getState }) => {
    const compose = getState().compose;
    const spoiler = compose.get('spoiler') as boolean;

    const params: ApiDraftParams = {
      text: (compose.get('text') as string | null) ?? '',
      spoiler_text: spoiler
        ? ((compose.get('spoiler_text') as string | null) ?? '')
        : '',
      visibility: compose.get('privacy') as string | null,
      sensitive: !!compose.get('sensitive'),
      language: compose.get('language') as string | null,
      in_reply_to_id: compose.get('in_reply_to') as string | null,
    };

    if (!params.text.trim() && !params.spoiler_text.trim()) {
      dispatch(showAlert({ message: messages.empty }));
      return;
    }

    const draftId = compose.get('draft_id') as string | null;

    if (savingDraft) return;
    savingDraft = true;

    try {
      const saved = draftId
        ? await apiUpdateDraft(draftId, params)
        : await apiCreateDraft(params);

      notifyDraftsChanged();

      // Typed on while the draft was being saved: keep the form, now as
      // that draft, so nothing typed is lost and the next save updates it
      const now = getState().compose;
      const spoilerNow = now.get('spoiler') as boolean;
      const unchanged =
        ((now.get('text') as string | null) ?? '') === params.text &&
        (spoilerNow
          ? ((now.get('spoiler_text') as string | null) ?? '')
          : '') === params.spoiler_text;

      if (!unchanged) {
        dispatch(setComposeDraftId(saved.id));
        dispatch(showAlert({ message: messages.saved }));
        return;
      }

      // The draft is safe on the server: clear the form (and the draft link)
      // so the next post is not saved over this draft
      dispatch(resetCompose());
      resetNgWordsWarning();
      dispatch(
        showAlert({
          message: messages.saved,
          action: messages.view,
          onClick: () => {
            browserHistory.push('/drafts');
          },
        }),
      );
    } catch (error) {
      dispatch(showAlertForError(error));
    } finally {
      savingDraft = false;
    }
  },
);

/**
 * Continues writing a draft in the compose form. The post it replies to is
 * loaded too, so the reply indicator shows it; if it was deleted, the draft
 * opens as a new post.
 */
export const openComposeDraft = createAppThunk(
  async (draft: ApiDraftJSON, { dispatch, getState }) => {
    let inReplyToId = draft.in_reply_to_id;

    if (inReplyToId && !getState().statuses.has(inReplyToId)) {
      try {
        const response = await api().get<ApiStatusJSON>(
          `/api/v1/statuses/${inReplyToId}`,
        );
        dispatch(importFetchedStatus(response.data));
      } catch {
        inReplyToId = null;
        dispatch(showAlert({ message: messages.replyGone }));
      }
    }

    dispatch(loadComposeDraft({ ...draft, in_reply_to_id: inReplyToId }));
    ensureComposeIsVisible(getState);
  },
);
